import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import * as React from "react";

export interface JobSummary {
  aircraft: { prod: string | null; worker1: string | null; worker2: string | null } | null;
  obstacles: { prod: string | null; worker1: string | null; worker2: string | null } | null;
  towing: { prod: string | null; worker1: string | null; worker2: string | null } | null;
}

/**
 * Type guard to validate JobSummary data at runtime
 * Ensures data from backend matches expected structure
 */
export function isValidJobSummary(data: any): data is JobSummary {
  if (!data || typeof data !== 'object') return false;

  // Check that each component is either null or an object with expected keys
  const validateComponent = (comp: any) => {
    if (comp === null) return true;
    if (typeof comp !== 'object') return false;
    return (
      ('prod' in comp) &&
      ('worker1' in comp) &&
      ('worker2' in comp)
    );
  };

  return (
    validateComponent(data.aircraft) &&
    validateComponent(data.obstacles) &&
    validateComponent(data.towing)
  );
}

const StatusCell: React.FC<{ status: string | null }> = ({ status }) => {
  let symbol = "—";
  let color = "text.disabled";

  if (status === "success") {
    symbol = "✓";
    color = "success.main";
  } else if (status === "skipped") {
    symbol = "○";
    color = "info.main";
  } else if (status === "error") {
    symbol = "✗";
    color = "error.main";
  }

  return (
    <Box sx={{ color, fontWeight: "bold", fontSize: "1rem" }}>
      {symbol}
    </Box>
  );
};

export const ExecutionDetailPanel: React.FC<{ summary: JobSummary | null }> = ({ summary }) => {
  if (!summary) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No execution details available
        </Typography>
      </Box>
    );
  }

  const components = [
    { key: "aircraft", label: "Aircraft", data: summary.aircraft },
    { key: "obstacles", label: "Obstacles", data: summary.obstacles },
    { key: "towing", label: "Towing Equip", data: summary.towing },
  ];

  return (
    <Box sx={{ p: 2, backgroundColor: "grey.50" }}>
      <Typography variant="subtitle2" gutterBottom>
        Execution Details
      </Typography>
      <Table size="small" sx={{ maxWidth: 400 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Component</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Prod</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Worker 1</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Worker 2</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {components.map(({ key, label, data }) => (
            <TableRow key={key}>
              <TableCell>{label}</TableCell>
              <TableCell sx={{ textAlign: "center" }}>
                <StatusCell status={data?.prod || null} />
              </TableCell>
              <TableCell sx={{ textAlign: "center" }}>
                <StatusCell status={data?.worker1 || null} />
              </TableCell>
              <TableCell sx={{ textAlign: "center" }}>
                <StatusCell status={data?.worker2 || null} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ color: "success.main", fontWeight: "bold" }}>✓</Box>
          Success
        </Typography>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ color: "info.main", fontWeight: "bold" }}>○</Box>
          Skipped
        </Typography>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ color: "error.main", fontWeight: "bold" }}>✗</Box>
          Error
        </Typography>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled" }}>
          <Box component="span" sx={{ fontWeight: "bold" }}>—</Box>
          N/A
        </Typography>
      </Box>
    </Box>
  );
};
